import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    AlertTriangle, ArrowLeft, ArrowRight, Check, Clock, 
    Heart, Volume2, VolumeX, RefreshCw, Home, Droplets,
    Hand, Sparkles, Wind
} from 'lucide-react';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, SubHeading, BodyText, SmallText } from '../components/brand/BrandTypography';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ConflictCrisis - Emergency de-escalation tool for couples in conflict
 * 
 * A guided, step-by-step path to help partners regulate during or after an argument
 */

const TECHNIQUE_ICONS = {
    cold_water: Droplets,
    wall_push: Hand,
    shake_it: RefreshCw,
    box_breath: Wind
};

const BreathingExercise = ({ onComplete }) => {
    const [phase, setPhase] = useState('inhale');
    const [count, setCount] = useState(4);
    const [cycles, setCycles] = useState(0);
    const totalCycles = 3;

    useEffect(() => {
        if (cycles >= totalCycles) {
            onComplete();
            return;
        }

        const timer = setInterval(() => {
            setCount(prev => {
                if (prev <= 1) {
                    // Move to next phase
                    setPhase(current => {
                        if (current === 'inhale') return 'hold1';
                        if (current === 'hold1') return 'exhale';
                        if (current === 'exhale') return 'hold2';
                        setCycles(c => c + 1);
                        return 'inhale';
                    });
                    return 4;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [cycles, onComplete]);

    const phaseText = {
        inhale: 'Breathe in...',
        hold1: 'Hold...',
        exhale: 'Breathe out...',
        hold2: 'Hold...'
    };

    return (
        <div className="flex flex-col items-center py-8">
            <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-1000 ${
                phase === 'inhale' ? 'scale-110 bg-[#7D8E95]/40' :
                phase === 'exhale' ? 'scale-90 bg-[#7D8E95]/40' :
                'scale-100 bg-[#FFBB98]/30'
            }`}>
                <span className="text-4xl font-light text-[#1D6B6B]">{count}</span>
            </div>
            <p className="mt-4 text-lg text-[#2A8585] font-medium">{phaseText[phase]}</p>
            <SmallText className="mt-2 text-[#7D8E95]">
                Breath {cycles + 1} of {totalCycles}
            </SmallText>
        </div>
    );
};

const StepCard = ({ step, currentSelections, onSelect, onComplete }) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [breathingComplete, setBreathingComplete] = useState(false);

    const handleSelect = (item) => {
        if (step.type === 'selection' || step.type === 'technique_selection') {
            setSelectedItems([item]);
            onSelect(step.id, item);
        }
    };

    const handleChecklistComplete = () => {
        onComplete();
    };

    if (step.type === 'breathing') {
        return (
            <div className="text-center">
                <Heading className="text-[#1D6B6B] mb-2">{step.title}</Heading>
                <BodyText className="text-[#7D8E95] mb-6">{step.instruction}</BodyText>
                
                {!breathingComplete ? (
                    <BreathingExercise onComplete={() => setBreathingComplete(true)} />
                ) : (
                    <div className="py-8">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#7D8E95]/30 flex items-center justify-center mb-4">
                            <Check className="w-8 h-8 text-[#1D6B6B]" />
                        </div>
                        <BodyText className="text-[#1D6B6B]">Well done. Let's continue.</BodyText>
                    </div>
                )}
            </div>
        );
    }

    if (step.type === 'action') {
        return (
            <div className="text-center">
                <Heading className="text-[#1D6B6B] mb-2">{step.title}</Heading>
                <BodyText className="text-[#2A8585] mb-4">{step.instruction}</BodyText>
                {step.note && (
                    <div className="bg-white/70 rounded-xl p-4 mt-4">
                        <SmallText className="text-[#7D8E95] italic">{step.note}</SmallText>
                    </div>
                )}
            </div>
        );
    }

    if (step.type === 'selection') {
        return (
            <div>
                <Heading className="text-[#1D6B6B] mb-2 text-center">{step.title}</Heading>
                <BodyText className="text-[#7D8E95] mb-6 text-center">{step.instruction}</BodyText>
                
                <div className="grid grid-cols-2 gap-3">
                    {step.options.map((option, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleSelect(option)}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                                selectedItems.includes(option)
                                    ? 'border-[#1D6B6B] bg-[#7D8E95]/20'
                                    : 'border-white/40 hover:border-[#7D8E95]'
                            }`}
                        >
                            <SmallText className="text-[#1D6B6B]">{option}</SmallText>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    if (step.type === 'technique_selection') {
        return (
            <div>
                <Heading className="text-[#1D6B6B] mb-2 text-center">{step.title}</Heading>
                <BodyText className="text-[#7D8E95] mb-6 text-center">{step.instruction}</BodyText>
                
                <div className="space-y-3">
                    {step.options.map((option) => {
                        const Icon = TECHNIQUE_ICONS[option.id] || Sparkles;
                        const isSelected = selectedItems.includes(option.id);
                        
                        return (
                            <button
                                key={option.id}
                                onClick={() => handleSelect(option.id)}
                                className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                                    isSelected
                                        ? 'border-[#1D6B6B] bg-[#7D8E95]/20'
                                        : 'border-white/40 hover:border-[#7D8E95]'
                                }`}
                            >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    isSelected ? 'bg-[#1D6B6B] text-white' : 'bg-white/70 text-[#7D8E95]'
                                }`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 text-left">
                                    <BodyText className="text-[#1D6B6B] font-medium">{option.name}</BodyText>
                                    <SmallText className="text-[#7D8E95]">{option.time}</SmallText>
                                </div>
                                {isSelected && <Check className="w-5 h-5 text-[#1D6B6B]" />}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (step.type === 'checklist') {
        return (
            <div>
                <Heading className="text-[#1D6B6B] mb-2 text-center">{step.title}</Heading>
                <BodyText className="text-[#7D8E95] mb-6 text-center">{step.instruction}</BodyText>
                
                <div className="space-y-4">
                    {step.questions.map((question, idx) => (
                        <div 
                            key={idx}
                            className="p-4 rounded-xl bg-white/70 border border-white/40"
                        >
                            <BodyText className="text-[#1D6B6B]">{question}</BodyText>
                        </div>
                    ))}
                </div>
                
                <div className="mt-6 text-center">
                    <SmallText className="text-[#7D8E95] italic">
                        If you answered "no" to any of these, take a few more minutes.
                    </SmallText>
                </div>
            </div>
        );
    }

    if (step.type === 'scripts') {
        return (
            <div>
                <Heading className="text-[#1D6B6B] mb-2 text-center">{step.title}</Heading>
                <BodyText className="text-[#7D8E95] mb-6 text-center">{step.instruction}</BodyText>
                
                <div className="space-y-3">
                    {step.scripts.map((script, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleSelect(script)}
                            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                                selectedItems.includes(script)
                                    ? 'border-[#1D6B6B] bg-[#7D8E95]/20'
                                    : 'border-white/40 hover:border-[#7D8E95]'
                            }`}
                        >
                            <BodyText className="text-[#1D6B6B] italic">"{script}"</BodyText>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return null;
};

export const ConflictCrisis = () => {
    const navigate = useNavigate();
    const [steps, setSteps] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [selections, setSelections] = useState({});
    const [loading, setLoading] = useState(true);
    const [complete, setComplete] = useState(false);

    useEffect(() => {
        const fetchSteps = async () => {
            try {
                const res = await fetch(`${API_URL}/api/crisis/steps`);
                if (res.ok) {
                    const data = await res.json();
                    setSteps(data.steps);
                }
            } catch (error) {
                console.error('Failed to fetch crisis steps:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSteps();
    }, []);

    const handleSelect = (stepId, value) => {
        setSelections(prev => ({ ...prev, [stepId]: value }));
    };

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            // Complete the crisis session
            setComplete(true);
            logCrisisSession();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const logCrisisSession = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            if (token) {
                await fetch(`${API_URL}/api/crisis/log`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.error('Failed to log crisis session:', error);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/40"></div>
            </div>
        );
    }

    if (complete) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FEF6EF] flex flex-col">
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="max-w-md w-full text-center">
                        <div className="w-20 h-20 mx-auto rounded-full bg-[#7D8E95]/30 flex items-center justify-center mb-6">
                            <Heart className="w-10 h-10 text-[#1D6B6B]" />
                        </div>
                        
                        <Heading className="text-[#1D6B6B] mb-4">You did something brave</Heading>
                        <BodyText className="text-[#7D8E95] mb-8">
                            Choosing to regulate instead of react takes real courage. 
                            Whatever happens next, you've already done the hardest part.
                        </BodyText>
                        
                        <div className="space-y-3">
                            <button
                                onClick={() => navigate('/home')}
                                className="w-full py-4 px-6 bg-[#1D6B6B] text-white rounded-xl font-medium hover:bg-[#1D6B6B] transition-colors"
                            >
                                Return Home
                            </button>
                            <button
                                onClick={() => navigate('/relationship-hub/repair')}
                                className="w-full py-4 px-6 bg-[#FFBB98]/50 text-[#8B6A6A] rounded-xl font-medium hover:bg-[#FFBB98] transition-colors"
                            >
                                Go to Repair Tools
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const step = steps[currentStep];
    const progress = ((currentStep + 1) / steps.length) * 100;

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FEF6EF] flex flex-col">
            {/* Header */}
            <header className="sticky top-0 z-10 glass-header px-4 py-3">
                <div className="max-w-2xl mx-auto flex items-center justify-between">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-[#FFBB98]/50 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#7D8E95]" />
                    </button>
                    
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-[#FFBB98]" />
                        <SmallText className="text-[#FFBB98] font-medium">Conflict Support</SmallText>
                    </div>
                    
                    <button
                        onClick={() => navigate('/home')}
                        className="p-2 hover:bg-[#FFBB98]/50 rounded-full transition-colors"
                    >
                        <Home className="w-5 h-5 text-[#7D8E95]" />
                    </button>
                </div>
            </header>

            {/* Progress Bar */}
            <div className="px-4 py-2">
                <div className="max-w-2xl mx-auto">
                    <div className="flex items-center justify-between mb-2">
                        <SmallText className="text-[#7D8E95]">Step {currentStep + 1} of {steps.length}</SmallText>
                    </div>
                    <div className="h-1.5 bg-[#FFBB98] rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-gradient-to-r from-[#FFBB98] to-[#D4A59A] transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="flex-1 px-4 py-6">
                <div className="max-w-2xl mx-auto">
                    <div className="glass-card rounded-2xl p-6">
                        <StepCard 
                            step={step}
                            currentSelections={selections}
                            onSelect={handleSelect}
                            onComplete={handleNext}
                        />
                    </div>
                </div>
            </main>

            {/* Navigation Footer */}
            <footer className="sticky bottom-0 bg-white/70/95 backdrop-blur-sm border-t border-white/40 px-4 py-4">
                <div className="max-w-2xl mx-auto flex items-center justify-between">
                    <button
                        onClick={handleBack}
                        disabled={currentStep === 0}
                        className="flex items-center gap-2 px-4 py-2 text-[#7D8E95] hover:text-[#1D6B6B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    
                    <button
                        onClick={handleNext}
                        className="flex items-center gap-2 px-6 py-3 bg-[#1D6B6B] text-white rounded-xl font-medium hover:bg-[#1D6B6B] transition-colors"
                    >
                        {currentStep === steps.length - 1 ? 'Complete' : 'Continue'}
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default ConflictCrisis;
